/**
 * Messaging domain contracts — Module 6 (TW-3453).
 *
 * A trip carries one conversation between the club and TC: a `message_thread` (one per trip, the
 * `@@unique(trip_id)`) holding `message` rows. The same thread is co-edited by both portals — the
 * club and TC read + post to it, and RLS scopes each side to its own club's thread — so these Zod
 * schemas + the unread fold are the single source of truth the API validates with and both the
 * club proposal view and the back-office Messages tab render from.
 *
 * The unread maths is a pure fold over the messages + the caller's last-read pointer + the caller's
 * id: a message counts as unread when it is newer than the pointer and the caller did not send it.
 * It is clock-free (it compares ISO strings the server already stamped) so it is deterministic and
 * unit-testable, exactly like the trip/manifest summaries.
 */
import { z } from "zod";

/** The two thread shapes share one table, distinguished by `type` (data-model §7). */
export type ThreadType = "TRIP" | "GENERAL";

/** Post a message to a thread. Body is trimmed, non-empty, and length-capped. */
export const postMessageSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});
export type PostMessageInput = z.infer<typeof postMessageSchema>;

/** A single message as returned to the client. */
export interface MessageDto {
  id: string;
  threadId: string;
  body: string;
  /** The author's user id; null for system-authored messages. */
  senderId: string | null;
  /** The author's display name, resolved server-side (null if the user is gone). */
  senderName: string | null;
  /**
   * Whether the author is a back-office (TC) user. The two-party chat renders by *side*, so the
   * client picks the bubble alignment from `senderIsBackOffice === myIsBackOffice` — every club
   * message sits on one side and every TC message on the other, regardless of which colleague sent it.
   */
  senderIsBackOffice: boolean;
  createdAt: string;
}

/** A trip (or general) thread plus the caller's read state — the shape the chat panel consumes. */
export interface MessageThreadDto {
  threadId: string;
  /** Null for a general (non-trip) club inbox thread; set for a trip-scoped thread. */
  tripId: string | null;
  type: ThreadType;
  messages: MessageDto[];
  /** The caller's last-read timestamp for this thread (null = never opened). */
  lastReadAt: string | null;
  /** Messages newer than `lastReadAt` that the caller did not send. */
  unreadCount: number;
}

/**
 * One row in the messages inbox (TW-3454): a thread the caller participates in — the club's general
 * thread or one of its trip threads — with just enough to render the list and its unread badge. The
 * full conversation is loaded on demand when a row is opened.
 */
export interface ThreadSummaryDto {
  threadId: string;
  type: ThreadType;
  /** The owning club — lets a back-office inbox open the right club's general thread. */
  clubId: string;
  /** Null for the general thread; the trip id for a trip thread (used to open its panel). */
  tripId: string | null;
  /** The trip's reference for a trip thread (e.g. "TC-2026-0007"); null for the general thread. */
  tripReference: string | null;
  /** A display title — the trip opponent/name (reference is carried separately), the club name for general. */
  title: string;
  /** The most recent message's body (truncated) + when it arrived; null for an empty thread. */
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  /** Messages newer than the caller's pointer that the caller did not send. */
  unreadCount: number;
}

/** Sum the unread counts across an inbox — the messages badge total (pure, deterministic). */
export function totalUnread(threads: readonly Pick<ThreadSummaryDto, "unreadCount">[]): number {
  return threads.reduce((sum, t) => sum + t.unreadCount, 0);
}

/**
 * Order an inbox's thread summaries for display: every GENERAL thread first — the club's
 * always-available conversation (a cross-club back-office inbox may carry one per club) — then the
 * trip threads. Within each group, most recent activity first; threads with no messages sort last
 * (their null timestamp compares as the empty string). Pure + deterministic, like
 * {@link totalUnread}, so the API and any client render the same order.
 */
export function sortInboxThreads<T extends Pick<ThreadSummaryDto, "type" | "lastMessageAt">>(
  threads: readonly T[],
): T[] {
  const rank = (t: T): number => (t.type === "GENERAL" ? 0 : 1);
  return [...threads].sort(
    (a, b) => rank(a) - rank(b) || (b.lastMessageAt ?? "").localeCompare(a.lastMessageAt ?? ""),
  );
}

/** The minimum of a message the unread fold needs (kept narrow so it is easy to test + reuse). */
export interface UnreadMessage {
  senderId: string | null;
  createdAt: string;
}

/**
 * Count the messages unread by `currentUserId`: those created strictly after their last-read
 * pointer that they did not author themselves. A null `lastReadAt` means the caller has never
 * opened the thread, so every message from someone else is unread. Comparing the ISO timestamps
 * the server stamped keeps this pure and deterministic (no clock read).
 */
export function countUnreadMessages(
  messages: readonly UnreadMessage[],
  lastReadAt: string | null,
  currentUserId: string,
): number {
  return messages.reduce((unread, m) => {
    if (m.senderId === currentUserId) return unread;
    if (lastReadAt !== null && m.createdAt <= lastReadAt) return unread;
    return unread + 1;
  }, 0);
}
